import {TaskServizi} from "@commons/mapping/TaskServizi";
import {PresenzeGiornataPlusRecord} from "@commons/mapping/PresenzeGiornataPlusRecord";
import {LavoratorePresenzeRecord} from "@commons/mapping/LavoratorePresenzeRecord";
import {RiepilogoOrePlus} from "@commons/mapping/RiepilogoOrePlus";
import {IndiceMese, ObjectFactory} from "@commons/types";
import {merge} from "lodash";
import {GestionePlus} from "@commons/services/presenze_calcolo/GestionePlus";
import {Calc, Coppia} from "@commons/mapping/Calc";
import {GiustificativoRecord} from "@commons/mapping/GiustificativoRecord";
import {PresenzeGiustificativo} from "@commons/mapping/PresenzeGiustificativo";
import {ConsolidatedData} from "@commons/services/presenze_calcolo/GestionePlusData";
import {CachePresenze} from "@commons/mapping/CachePresenze";

export interface GiustificazioneDiMassaTask extends TaskServizi {

    tipoOperazione: 'INSERIMENTO' | 'MODIFICA' | 'CANCELLAZIONE',

    codGiu: string,
    codGiuDaSostituire: string,
    controllo: 'NESSUN_CONTROLLO' | 'COME_PREVISTO' | 'COME_MANCANTE' | 'COME_INTERVALLO',
    ore: number | null, // float, hhcc o importo
    azioneXGiuPresente: 'SALTA' | 'AGGIUNGI' | 'SOTTRAI' | 'SOSTITUISCI',

    calcolaPresenze: boolean,
    elaboraGiorniBloccati: boolean,
    livelloPresenzeChiusura: number; // Livello presenze chiusura dell'utente che sta eseguendo la giustificazione di massa!

    salvaPresenze: boolean,
    controllaIntervalloLavorato: boolean,
    intervalloInizio: string, // hhmm
    intervalloFine: string, // hhmm
    soloStandard: boolean,

    // Opzioni specifiche per modalità standard - INIZIO
    selezioneAutomatica: boolean,
    giornate: number,
    soloConPrevisto: boolean,
    // Opzioni specifiche per modalità standard - FINE
}

export class GiustificazioneDiMassaTask extends TaskServizi {
    static elencoOperazioni = [
        {
            'id': 'INSERIMENTO',
            'descrizione': 'Inserimento'
        },
        {
            'id': 'MODIFICA',
            'descrizione': 'Modifica'
        },
        {
            'id': 'CANCELLAZIONE',
            'descrizione': 'Cancellazione'
        }
    ];

    static elencoControllo = [
        {
            'id': 'NESSUN_CONTROLLO',
            'descrizione': 'Come quantità indicata',
            'standard': true,
            'plus': true
        },
        {
            'id': 'COME_PREVISTO',
            'descrizione': 'Come previsto',
            'standard': true,
            'plus': true
        },
        {
            'id': 'COME_MANCANTE',
            'descrizione': 'Come mancante',
            'standard': false,
            'plus': true
        },
        {
            'id': 'COME_INTERVALLO',
            'descrizione': 'Come da intervallo lavorato (ARR)',
            'standard': false,
            'plus': true
        },
        {
            'id': 'COME_INTERVALLO_EFF',
            'descrizione': 'Come da intervallo lavorato (EFF)',
            'standard': false,
            'plus': true
        },
        {
            'id': 'COME_ORDINARIO',
            'descrizione': 'Come totale ordinario',
            'standard': false,
            'plus': true
        },
        {
            'id': 'COME_ORDINARIO_D',
            'descrizione': 'Come ordinario diurno',
            'standard': false,
            'plus': true
        },
        {
            'id': 'COME_ORDINARIO_N',
            'descrizione': 'Come ordinario notturno',
            'standard': false,
            'plus': true
        },
        {
            'id': 'COME_EXTRA',
            'descrizione': 'Come totale eccedente',
            'standard': false,
            'plus': true
        },
        {
            'id': 'COME_EXTRA_D',
            'descrizione': 'Come eccedente diurno',
            'standard': false,
            'plus': true
        },
        {
            'id': 'COME_EXTRA_N',
            'descrizione': 'Come eccedente notturno',
            'standard': false,
            'plus': true
        }
    ];

    static massaControlloGiustificativo = [
        {
            'id': 'SALTA',
            'descrizione': 'Salta giornata'
        },
        {
            'id': 'AGGIUNGI',
            'descrizione': 'Aggiungi ore/quantità'
        },
        {
            'id': 'SOTTRAI',
            'descrizione': 'Sottrai ore/quantità'
        },
        {
            'id': 'SOSTITUISCI',
            'descrizione': 'Sostituisci'
        }
    ];

    static factory: ObjectFactory<GiustificazioneDiMassaTask> = (json?) => {
        return merge<GiustificazioneDiMassaTask, Partial<GiustificazioneDiMassaTask> | undefined>({
            // da TaskServizi
            idAziendaMaster: 0,
            idAzienda: 0,
            anno: 0,
            mese: 0,
            ggIniziale: null,
            ggFinale: null,
            eseguiPer: 'TUTTI',
            idDipSingolo: null,
            agenziaInterinale: null,
            spaccatura: 'NESSUNA',
            codiceCDIC: null,
            dataIniziale: null,
            dataFinale: null,
            result: null,
            stato: 'IN_ATTESA',

            // Specifici di <GiustificazioneDiMassa>
            tipoOperazione: 'INSERIMENTO',
            codGiu: '',
            codGiuDaSostituire: '',
            controllo: 'COME_MANCANTE',
            ore: null,
            azioneXGiuPresente: 'SALTA',
            calcolaPresenze: true,
            elaboraGiorniBloccati: false,
            livelloPresenzeChiusura: 0,

            salvaPresenze: false,
            controllaIntervalloLavorato: false,
            intervalloInizio: '',
            intervalloFine: '',
            soloStandard: false,

            // Opzioni specifiche per modalità standard - INIZIO
            selezioneAutomatica: false,
            giornate: 0,
            soloConPrevisto: false,

            URI: null,
        }, json);
    }

    static isGMCompliance = function (that: GiustificazioneDiMassaTask, day: PresenzeGiornataPlusRecord, lavoratore: Pick<LavoratorePresenzeRecord, 'proprieta'>) {
        let isCompliance = false;
        //if (day.stato < this.livelloPresenzeChiusura && (day.giornataBloccata === false || this.elaboraGiorniBloccati)) {
        //10/12/2021
        const isGiornataCalcolabile = (!day.giornataBloccata || that.elaboraGiorniBloccati);
        if (isGiornataCalcolabile && !PresenzeGiornataPlusRecord.isDayChiuso(day, that.livelloPresenzeChiusura, that.mese as IndiceMese, lavoratore)) {
            if (that.selezioneAutomatica) {
                isCompliance = true;
            } else {
                if (that.soloConPrevisto && (RiepilogoOrePlus.getTotaleHPrev(day.totali) === 0)) {
                    isCompliance = false;
                } else {
                    //const pos = DateTime.fromISO(day.dtGiornata!).weekday; // Get the day of the week. 1 is Monday and 7 is Sunday
                    const pos = PresenzeGiornataPlusRecord.getDayOfWeek(day); // Get the day of the week. 1 is Monday and 7 is Sunday
                    const flagDay = 1 << (pos - 1);
                    isCompliance = (that.giornate & flagDay) > 0;
                }
            }
        }
        if (isCompliance) {
            console.log('isGMCompliance, isCompliance', day.dtGiornata);
        }
        return isCompliance;
    };

    static isInserimento = (that: GiustificazioneDiMassaTask) => that.tipoOperazione === 'INSERIMENTO';

    static isModifica = (that: GiustificazioneDiMassaTask) => that.tipoOperazione === 'MODIFICA';

    static isCancellazione = (that: GiustificazioneDiMassaTask) => that.tipoOperazione === 'CANCELLAZIONE';

    static reset = function (that: GiustificazioneDiMassaTask, json?: Partial<GiustificazioneDiMassaTask>) {
        return merge<GiustificazioneDiMassaTask, Partial<GiustificazioneDiMassaTask> | undefined>(that, GiustificazioneDiMassaTask.factory(json));
    };

    static calcolarePrimaDiGiustificare = (gm: GiustificazioneDiMassaTask, day: PresenzeGiornataPlusRecord, gestione: ConsolidatedData, cache: CachePresenze) => {
        let calcolaPrimaDiGiustificare = false;
        const giu = cache.getGiustificativo(gm.codGiu)!;
        const isGiuOrario = GiustificativoRecord.isOrario(giu);
        switch (gm.tipoOperazione) {
            case 'INSERIMENTO':
                // Se tipo di controllo su inserimento non è Nessun Controllo (occorre cioè inserire la quantità indicata), allora...
                if (isGiuOrario && gm.controllo !== 'NESSUN_CONTROLLO') {
                    if (gestione.proprietaGestione?.quantitaTimbrature === 'CON_O_SENZA') {
                        if (day.effettive.length === 0) {
                            if (day.tipoRilevazione !== 'MANUALE') {
                                day.arrotondateLocked = false;
                            }
                            PresenzeGiornataPlusRecord.eraseTimbrature(day, day.arrotondate);
                            calcolaPrimaDiGiustificare = true;
                        }
                    }
                    if (gm.calcolaPresenze || calcolaPrimaDiGiustificare) {
                        //gestione.calcolaGiornata(day, day.arrotondateLocked, false, true);
                        return true;
                    }
                }
                break;

            default:
                break;
        }
        return false;
    }
    /**
     * Giustificazione di massa per singola giornata
     */
    static giustificazioneMassaDay = function (gm: GiustificazioneDiMassaTask, day: PresenzeGiornataPlusRecord, {distribuzioneTurno}: ConsolidatedData, cache: CachePresenze, _interattivo: boolean) {
        const giu = cache.getGiustificativo(gm.codGiu)!;
        const isGiuOrario = GiustificativoRecord.isOrario(giu);
        const causali = isGiuOrario ? day.causaliHH : day.causaliQI;
        switch (gm.tipoOperazione) {
            case 'INSERIMENTO': {
                let causale = null;
                let posGiu = -1;
                for (let indGiu = 0; indGiu < causali.length; ++indGiu) {
                    causale = causali[indGiu];
                    if (causale && PresenzeGiustificativo.isValid(causale)) {
                        if (causale.codGiu === gm.codGiu) {
                            posGiu = indGiu;
                        }
                    }
                }

                let setupCausale = false;
                if (causale && posGiu >= 0 && gm.ore) {
                    switch (gm.azioneXGiuPresente) {
                        case 'AGGIUNGI':
                            causali.splice(posGiu, 1);
                            causale.quantita += gm.ore;
                            PresenzeGiornataPlusRecord.addCausaliHH(day, [causale], GestionePlus.maxCausaliOre);
                            break;

                        case 'SOTTRAI':
                            causali.splice(posGiu, 1);
                            causale.quantita -= gm.ore;
                            PresenzeGiornataPlusRecord.addCausaliHH(day, [causale], GestionePlus.maxCausaliOre);
                            break;

                        case 'SOSTITUISCI':
                            causali.splice(posGiu, 1);
                            setupCausale = true;
                            break;

                        case 'SALTA': // nn faccio nulla
                        default:
                            break;
                    }
                } else {
                    setupCausale = true;
                }

                causale = null;
                if (setupCausale) {
                    if (isGiuOrario) {
                        let forceGMCompliance = false;
                        let quantita = 0;
                        switch (gm.controllo) {
                            case 'COME_PREVISTO':
                                quantita = RiepilogoOrePlus.getTotaleHPrev(day.totali) / 60;
                                forceGMCompliance = (quantita > 0);
                                break;
                            case 'COME_MANCANTE':
                                quantita = PresenzeGiornataPlusRecord.getMancante(day, cache, false, distribuzioneTurno!);
                                forceGMCompliance = (quantita > 0);
                                break;

                            case 'COME_INTERVALLO': // todo, da implementare???
                                quantita = parseFloat('' + gm.ore);
                                forceGMCompliance = (quantita > 0);
                                break;

                            case 'NESSUN_CONTROLLO':
                            default:
                                quantita = parseFloat('' + gm.ore);
                                break;
                        }
                        if ((forceGMCompliance || GiustificativoRecord.isGMCompliance(giu, day)) && GiustificativoRecord.isDayCompliance(giu, day)) {
                            if (GiustificativoRecord.isRangeValid(giu, quantita * 60, RiepilogoOrePlus.getTotaleHPrev(day.totali), false)) {
                                causale = PresenzeGiustificativo.factory({codGiu: gm.codGiu, tipoGiu: GiustificativoRecord.getTipoGiu(giu)});
                                PresenzeGiustificativo.setHHCC(causale, quantita);
                            }
                        }
                    } else {
                        causale = PresenzeGiustificativo.factory({codGiu: gm.codGiu, tipoGiu: GiustificativoRecord.getTipoGiu(giu)});
                        causale.quantita = parseFloat('' + gm.ore);
                    }
                }
                if (causale !== null) {
                    if (isGiuOrario)
                        PresenzeGiornataPlusRecord.addCausaliHH(day, [causale], GestionePlus.maxCausaliOre)
                    else
                        PresenzeGiornataPlusRecord.addCausaliQI(day, [causale], GestionePlus.maxCausaliQI);
                }
            }
                break;

            case 'MODIFICA': {
                let posGiuDaSostituire = -1;
                let posGiu = -1;
                for (let indGiu = 0; indGiu < causali.length; ++indGiu) {
                    const causale = causali[indGiu];
                    if (causale && PresenzeGiustificativo.isValid(causale)) {

                        if (causale.codGiu === gm.codGiuDaSostituire) {
                            posGiuDaSostituire = indGiu;
                        }
                        if (causale.codGiu === gm.codGiu) {
                            posGiu = indGiu;
                        }
                    }
                }
                if (posGiuDaSostituire >= 0) {
                    if (posGiu >= 0) { // Giustificativo gia presente
                        if (isGiuOrario) {
                            const hhcc = causali[posGiu]!.quantita + causali[posGiuDaSostituire]!.quantita;
                            PresenzeGiustificativo.setHHCC(causali[posGiu]!, hhcc);
                        } else {
                            causali[posGiu]!.quantita += causali[posGiuDaSostituire]!.quantita;
                        }
                        // rimuovo giu da sostituire
                        causali.splice(posGiuDaSostituire, 1);
                    } else {
                        causali[posGiuDaSostituire]!.codGiu = gm.codGiu;
                    }
                }
            }
                break;

            case 'CANCELLAZIONE':
                for (let indGiu = 0; indGiu < causali.length; ++indGiu) {
                    let causale = causali[indGiu];
                    if (causale && PresenzeGiustificativo.isValid(causale)) {
                        if (causale.codGiu === gm.codGiu) {
                            causali.splice(indGiu, 1);
                            break;
                        }
                    }
                }
                break;
        }
    };

    public static isPresenteDalleAlle = (gm: GiustificazioneDiMassaTask, day: PresenzeGiornataPlusRecord): boolean => {
        //const copieArr = day.getCopieEffettive();
        const copieArr = PresenzeGiornataPlusRecord.getCopieArrotondate(day);
        const calcLavorato = new Calc()
        calcLavorato.addCoppie(copieArr);
        /*
                for (let indE = 0; indE < copieArr.length; ++indE) {
                    calcLavorato.add(copieArr[indE].calc.getFlat());
                }*/
        const intervalloDaTestare = new Coppia(parseInt(gm.intervalloInizio), parseInt(gm.intervalloFine));
        const quantita = calcLavorato.getCount(intervalloDaTestare.calc);
        return quantita >= intervalloDaTestare.calc.getCount();
    };
}